import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Subscription } from 'rxjs';
import { ColumnDefinition, ColumnVisibility } from '../../../core/model/column-definition';
import { ExpandedListViewItemDefinition } from '../../../core/model/expanded-list-view-item';
import { ColumnDefinitionFactory } from '../../../core/service/factory/column-definition-factory.service';
import { FormControlExFactory } from '../../../core/service/factory/form-control-ex-factory.service';
import { NativeWindowService } from '../../../core/service/native-window.service';
import { ArticleService } from '../../../rent/service/data/article.service';
import { CompanyService } from '../../../rent/service/data/company.service';
import { PropertyService } from '../../../rent/service/data/property.service';
import { ReportService } from '../../service/data/report.service';
import { LocalDate } from '../../../core/model/local-date';
import { canSubmitReportForm } from '../../utility/report-form-validation';

@Component({
    selector: 'app-invoiced-per-rental-object',
    templateUrl: './invoiced-per-rental-object.component.html',
    styleUrls: ['./invoiced-per-rental-object.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class InvoicedPerRentalObjectComponent implements OnInit {
  constructor(
    private _cdf: ColumnDefinitionFactory,
    companyService: CompanyService,
    propertyService: PropertyService,
    private _nativeWindow: NativeWindowService,
    private _dataService: ReportService,
    fb: FormBuilder,
    fcf: FormControlExFactory
  ) {
    this.companyService = companyService;
    this.propertyService = propertyService;
    let startPeriod = LocalDate.fromDate(LocalDate.fromDate(LocalDate.today()).firstDayInMonth());
    let endPeriod = LocalDate.fromDate(startPeriod.lastDayInMonth());
    if (LocalDate.today().day >= 15) {
      startPeriod = LocalDate.fromDate(LocalDate.fromDate(LocalDate.today().addMonths(1)).firstDayInMonth());
      endPeriod = LocalDate.fromDate(startPeriod.lastDayInMonth());
    }

    this.form = fb.group({
      company: fcf.create('COMMON.COMPANY', null),
      property: fcf.create('COMMON.PROPERTY', null),
      periodFromFrom: fcf.createMandatory('INVOICE.PERIOD_FROM', startPeriod),
      periodFromTo: fcf.createMandatory('INVOICE.PERIOD_TO', endPeriod),
    });

    this.columnDefinitions = [
      this._cdf.createMoney('INVOICE.PREVIEW.AMOUNT', 'amount'),
      this._cdf.createMoney('INVOICE.PREVIEW.VAT', 'vatAmount'),
      this._cdf.createMoney('INVOICE.PREVIEW.AMOUNT_WITH_VAT', 'amountInclVat'),
    ];
  }

  propertyService: PropertyService;
  companyService: CompanyService;

  columnDefinitions: ColumnDefinition[] = [];
  
  dataContext: ExpandedListViewItemDefinition[] = [];
  form: FormGroup;
  today = new Date();
  private _subscription: Subscription;
  protected expandAllRows: boolean = false;

  ngOnInit() {}

  onPrintPages() {
    if (!canSubmitReportForm(this.form)) return;
    window.print();
  }

  exportExcel(): void {
    if (this._subscription) {
      this._subscription.unsubscribe();
    }
    this._subscription = this._dataService.getInvoicedPerRentalObjectExcel(this.form.value).subscribe(file => {
      this._nativeWindow.save(file);
    });
  }

  ngOnDestroy(): void {
    if (this._subscription) this._subscription.unsubscribe();
  }

  toggleExpandAllRows() {
    this.expandAllRows = !this.expandAllRows;
  }

  onSubmit() {
    this.expandAllRows = false;
    if (!canSubmitReportForm(this.form)) return;

    this._dataService.getInvoicedPerRentalObjectReport(this.form.value).subscribe(data => {
      this.dataContext = data
        ? data.map(overview => {
            const model = new ExpandedListViewItemDefinition();
            model.caption = overview.level1Name;
            model.columnDefinitions = [
              this._cdf.create('ARTICLE.EDIT_HEADER', 'level2Name'),
              this._cdf.create('INVOICE.RECIPIENT_NAME', 'invoiceRecipientName'),
              this._cdf.create('LEASE_AGREEMENT.LEASE_AGREEMENT_NUMBER', 'leaseAgreementNumber', ColumnVisibility.optional),
              this._cdf.create('INVOICE.OCRNUMBER', 'ocrNumber'),
              this._cdf.create('INVOICE.PERIOD', 'invoiceRowPeriod'),
              this._cdf.createDate('INVOICE.PERIOD_FROM', 'invoiceRowFromDate', ColumnVisibility.optional),
              this._cdf.createDate('INVOICE.PERIOD_TO', 'invoiceRowToDate', ColumnVisibility.optional),
              this._cdf.createMoney('INVOICE.PREVIEW.AMOUNT', 'amount'),
              this._cdf.createMoney('INVOICE.PREVIEW.VAT', 'vatAmount'),
              this._cdf.createMoney('INVOICE.PREVIEW.AMOUNT_WITH_VAT', 'amountInclVat'),
            ];
            model.dataContext = overview;
            model.itemsSource = overview.invoicedPerRentalObjectLevel2Models.map(x => {
              return {
                level2Name: x.level2Name,
                invoiceRecipientName: x.invoiceRecipientName,
                leaseAgreementNumber: x.leaseAgreementNumber,
                ocrNumber: x.ocrNumber,
                invoiceRowPeriod: x.invoiceRowPeriod,
                invoiceRowFromDate: x.invoiceRowFromDate,
                invoiceRowToDate: x.invoiceRowToDate,
                amount: x.amount,
                vatAmount: x.vatAmount,
                amountInclVat: x.amountInclVat,
              };
            });

            return model;
          })
        : [];
    });
  }
}
